import React, { useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { exportApi } from '../../api/services';
import { reportsCopy } from '../../copy/reports';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

// ---------------------------------------------------------------------------
// WeeklyExportDialog — collects an optional leader's note and three narrative
// lines, then triggers the server-rendered weekly PDF download. The note is
// pinned in localStorage so the next export pre-fills with the last sentence
// the executive wrote.
// ---------------------------------------------------------------------------

const PINNED_NOTE_KEY = 'cadence_leader_note_pinned';

function readPinnedNote(): string {
  try { return localStorage.getItem(PINNED_NOTE_KEY) ?? ''; } catch { return ''; }
}

function writePinnedNote(note: string) {
  try {
    if (note.trim()) {
      localStorage.setItem(PINNED_NOTE_KEY, note);
    } else {
      localStorage.removeItem(PINNED_NOTE_KEY);
    }
  } catch { /* private-browsing / quota-exhausted */ }
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
}

function filenameFromContentDisposition(headerValue: string | undefined, fallback: string): string {
  if (!headerValue) return fallback;
  const rfc5987 = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (rfc5987?.[1]) return decodeURIComponent(rfc5987[1].trim().replace(/"/g, ''));
  const legacy = headerValue.match(/filename="?([^"]+)"?/i);
  return legacy?.[1] ?? fallback;
}

interface WeeklyExportDialogProps {
  open: boolean;
  onClose: () => void;
  date: string;
  suggestion: { whatHappened: string; whatItMeans: string; whatsNext: string };
  forExecId?: string;
}

export default function WeeklyExportDialog({
  open,
  onClose,
  date,
  suggestion,
  forExecId,
}: WeeklyExportDialogProps) {
  const [whatHappened, setWhatHappened] = useState(suggestion.whatHappened);
  const [whatItMeans, setWhatItMeans] = useState(suggestion.whatItMeans);
  const [whatsNext, setWhatsNext] = useState(suggestion.whatsNext);
  const [leaderNote, setLeaderNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setWhatHappened(suggestion.whatHappened);
    setWhatItMeans(suggestion.whatItMeans);
    setWhatsNext(suggestion.whatsNext);
    setLeaderNote(readPinnedNote());
    setError(null);
  }, [open, suggestion]);

  const handleClearPin = () => {
    writePinnedNote('');
    setLeaderNote('');
  };

  const handleExport = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        leaderNote: leaderNote.trim() || undefined,
        whatHappened: whatHappened.trim() || undefined,
        whatItMeans: whatItMeans.trim() || undefined,
        whatsNext: whatsNext.trim() || undefined,
      };
      const response = await exportApi.downloadWeeklySummaryPdf(date, payload, forExecId);
      const blob = response.data as Blob;
      const filename = filenameFromContentDisposition(
        response.headers?.['content-disposition'] as string | undefined,
        `Cadence-WeeklySummary-${date}.pdf`,
      );
      triggerBrowserDownload(blob, filename);
      writePinnedNote(leaderNote);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? reportsCopy.exportFailed);
    } finally {
      setBusy(false);
    }
  };

  const hasPinnedNote = readPinnedNote().length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{reportsCopy.dialogTitle}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{reportsCopy.dialogBody}</p>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="w-happened">{reportsCopy.whatHappenedLabel}</Label>
            <Textarea
              id="w-happened"
              rows={2}
              value={whatHappened}
              onChange={(e) => setWhatHappened(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="w-means">{reportsCopy.whatItMeansLabel}</Label>
            <Textarea
              id="w-means"
              rows={2}
              value={whatItMeans}
              onChange={(e) => setWhatItMeans(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="w-next">{reportsCopy.whatsNextLabel}</Label>
            <Textarea
              id="w-next"
              rows={2}
              value={whatsNext}
              onChange={(e) => setWhatsNext(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="w-note">{reportsCopy.leaderNoteLabel}</Label>
            <Textarea
              id="w-note"
              rows={2}
              value={leaderNote}
              onChange={(e) => setLeaderNote(e.target.value)}
              disabled={busy}
              placeholder={reportsCopy.leaderNotePlaceholder}
            />
            {hasPinnedNote && (
              <p className="text-xs text-muted-foreground">{reportsCopy.pinnedNoteSaved}</p>
            )}
          </div>
          {hasPinnedNote && (
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" onClick={handleClearPin} disabled={busy}>
                {reportsCopy.clearPinnedNote}
              </Button>
            </div>
          )}
        </div>
        {error && (
          <Alert variant="error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {reportsCopy.cancel}
          </Button>
          <Button onClick={handleExport} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            {busy ? reportsCopy.exportingPdf : reportsCopy.export}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
